"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APIPaywallObservationWindowSchema = exports.GetSchedulesByItemIdsRequestSchema = exports.APIRecommendationSchema = exports.APIRoutineItemResponseSchema = exports.APITreatmentSchema = exports.APIRoutineStateSchema = exports.APIRoutineItemSchema = exports.ObservationIntelligenceSchema = exports.APIEndRoutineItemRequestSchema = exports.APISetRoutineStatesRequestSchema = exports.APISetRoutineStateRequestSchema = exports.APICreateTreatmentsRequestSchema = exports.APICreateRoutineItemsRequestSchema = void 0;
const zod_1 = require("zod");
const enums_1 = require("./enums");
// ===== REQUEST =====
exports.APICreateRoutineItemsRequestSchema = zod_1.z.object({
    products: zod_1.z
        .array(zod_1.z.object({
        id: zod_1.z.string(), // From Algolia
    }))
        .min(1, 'At least one product is required'),
    schedule: zod_1.z
        .object({
        days: zod_1.z.array(zod_1.z.object({
            day: zod_1.z.enum(enums_1.dayOfWeekEnum),
            periods: zod_1.z.array(zod_1.z.enum(enums_1.periodEnum)).min(1).max(2),
        })),
        asNeeded: zod_1.z.boolean(),
        occurredAt: zod_1.z.date(),
    })
        .refine((data) => data.days.length > 0 || data.asNeeded, 'Either select days or mark as needed'),
});
exports.APICreateTreatmentsRequestSchema = zod_1.z.object({
    treatments: zod_1.z
        .array(zod_1.z.object({
        id: zod_1.z.string(), // From Algolia
    }))
        .min(1, 'At least one treatment is required'),
    occurredAt: zod_1.z.date(),
});
// ===== STATE =====
exports.APISetRoutineStateRequestSchema = zod_1.z.object({
    itemId: zod_1.z.number(),
    date: zod_1.z.date(),
    period: zod_1.z.enum(enums_1.periodEnum),
    status: zod_1.z.enum(enums_1.routineMarkStatusEnum),
});
/** Mark several routine items the same way in one atomic write (e.g. the home
 * "Today's Routine" line logging a whole period at once). */
exports.APISetRoutineStatesRequestSchema = zod_1.z.object({
    // Bounded like `periods` above: a single period's routine never holds dozens
    // of items, so cap the bulk write to keep one request from fanning out into
    // an unbounded multi-row upsert.
    itemIds: zod_1.z.array(zod_1.z.number()).min(1).max(50),
    date: zod_1.z.date(),
    period: zod_1.z.enum(enums_1.periodEnum),
    status: zod_1.z.enum(enums_1.routineMarkStatusEnum),
});
exports.APIEndRoutineItemRequestSchema = zod_1.z.object({
    itemId: zod_1.z.number(),
    endReason: zod_1.z.enum(enums_1.routineEndReasonEnum).optional().default('stopped_using'),
});
// ===== OBSERVATION INTELLIGENCE =====
exports.ObservationIntelligenceSchema = zod_1.z.object({
    weeksElapsed: zod_1.z.number(),
    totalWeeks: zod_1.z.number(),
    milestoneReached: zod_1.z.boolean(),
    signalHeadline: zod_1.z.string().nullable(),
    signalDirection: zod_1.z.enum(['improving', 'stable', 'declining']).nullable(),
});
// ===== RESPONSE =====
exports.APIRoutineItemSchema = zod_1.z.object({
    id: zod_1.z.number(),
    schedules: zod_1.z.array(zod_1.z.object({
        day: zod_1.z.enum(enums_1.dayOfWeekEnum),
        period: zod_1.z.enum(enums_1.periodEnum),
    })),
    asNeeded: zod_1.z.boolean(),
    occurredAt: zod_1.z.date(),
    endedAt: zod_1.z.date().nullable(),
    endReason: zod_1.z.enum(enums_1.routineEndReasonEnum).nullable(),
    skinMatchScore: zod_1.z.number().nullable(),
    scoreAt: zod_1.z.date().nullable(),
    product: zod_1.z
        .object({
        id: zod_1.z.number(),
        title: zod_1.z.string(),
        fullTitle: zod_1.z.string(),
        alias: zod_1.z.string(),
        imageUrl: zod_1.z.string().nullable(),
        brandName: zod_1.z.string().nullable(),
        brandAlias: zod_1.z.string().nullable(),
        routineSlot: zod_1.z.enum(enums_1.routineSlotEnum).nullable(),
        ingredients: zod_1.z.string().nullable(),
    })
        .nullable(),
    createdAt: zod_1.z.date(),
    updatedAt: zod_1.z.date(),
    observationIntelligence: exports.ObservationIntelligenceSchema.optional(),
});
exports.APIRoutineStateSchema = zod_1.z.object({
    itemId: zod_1.z.number(),
    markAt: zod_1.z.date(),
    period: zod_1.z.enum(enums_1.periodEnum),
    status: zod_1.z.enum(enums_1.routineMarkStatusEnum),
});
exports.APITreatmentSchema = zod_1.z.object({
    id: zod_1.z.number(),
    occurredAt: zod_1.z.date(),
    treatment: zod_1.z.object({
        id: zod_1.z.number(),
        name: zod_1.z.string(),
        slug: zod_1.z.enum(enums_1.treatmentSlugEnum),
        category: zod_1.z.enum(enums_1.treatmentCategoryEnum),
        area: zod_1.z.array(zod_1.z.enum(enums_1.treatmentAreaEnum)),
    }),
    createdAt: zod_1.z.date(),
    updatedAt: zod_1.z.date(),
    observationIntelligence: exports.ObservationIntelligenceSchema.optional(),
});
exports.APIRoutineItemResponseSchema = zod_1.z.object({
    items: zod_1.z.array(exports.APIRoutineItemSchema),
    treatments: zod_1.z.array(exports.APITreatmentSchema),
    states: zod_1.z.array(exports.APIRoutineStateSchema),
    scanAts: zod_1.z.array(zod_1.z.date()),
});
// ===== RECOMMENDATIONS =====
exports.APIRecommendationSchema = zod_1.z.object({
    id: zod_1.z.number(),
    recommendationId: zod_1.z.string(),
    reportId: zod_1.z.string(),
    productId: zod_1.z.number(),
    // Product details (joined from pdb_product and pdb_brand)
    shopifyHandle: zod_1.z.string().nullable(),
    productName: zod_1.z.string(),
    productImageUrl: zod_1.z.string().nullable(),
    brandName: zod_1.z.string().nullable(),
    // Gap context
    gap: zod_1.z.string(),
    techName: zod_1.z.enum(enums_1.techNameEnum),
    targetTechNames: zod_1.z.array(zod_1.z.enum(enums_1.techNameEnum)),
    priority: zod_1.z.enum(enums_1.routineGapPriorityEnum),
    // Product selection details
    source: zod_1.z.enum(enums_1.productSourceEnum),
    matchScore: zod_1.z.number(),
    qualitySensitivity: zod_1.z.enum(enums_1.qualitySensitivityEnum),
    selectionRationale: zod_1.z.string(),
    matchRationale: zod_1.z.string(),
    // Lifecycle
    status: zod_1.z.enum(enums_1.recommendationStatusEnum),
    appliedAt: zod_1.z.date().nullable(),
    dismissedAt: zod_1.z.date().nullable(),
    // Timestamps
    createdAt: zod_1.z.date(),
    updatedAt: zod_1.z.date(),
});
// ===== SCHEDULE LOOKUP =====
/**
 * Request schema for fetching schedules by routine item IDs
 */
exports.GetSchedulesByItemIdsRequestSchema = zod_1.z.object({
    routineItemIds: zod_1.z.array(zod_1.z.number()),
});
// ===== PAYWALL OBSERVATION WINDOWS =====
/**
 * A single entry for a Day-1 user's "What Stella is Watching" view.
 * Returned by trpc.v1.user.routine.getObservationWindowsPreview.
 *
 * Discriminated by `state`:
 *   - `'observing'` — has a real MECHANISM_MATRIX observation window
 *     (post-scan addition with recognized active ingredients).
 *   - `'established'` — already part of the user's baseline. Either the
 *     item started long enough before the first scan that the
 *     too_early window has elapsed, OR the product has no recognized
 *     active ingredients (a gentle staple Stella already treats as
 *     part of the user's skin).
 *   - `'protective'` — sunscreen-slot product. Surfaces in its own
 *     section explaining that sunscreen is protective rather than
 *     measurable, regardless of when the user started it.
 *
 * All three states surface in the same array so the user gets a 1:1
 * mapping with every product they added — no silent drops.
 */
const APIPaywallObservationWindowBaseSchema = zod_1.z.object({
    /** routine_item.id — stable key for the frontend */
    itemId: zod_1.z.number(),
    productId: zod_1.z.number(),
    productName: zod_1.z.string(),
    brandName: zod_1.z.string().nullable(),
    routineSlot: zod_1.z.enum(enums_1.routineSlotEnum).nullable(),
    /** When the user added this product (user_routine_item.started_at) */
    startedAt: zod_1.z.date(),
    /** User's first completed scan date — frontend uses this as the "today" anchor */
    anchorDate: zod_1.z.date(),
});
exports.APIPaywallObservationWindowSchema = zod_1.z.discriminatedUnion('state', [
    APIPaywallObservationWindowBaseSchema.extend({
        state: zod_1.z.literal('observing'),
        /** Metrics this product is expected to affect (primary + secondary from MECHANISM_MATRIX) */
        targetMetrics: zod_1.z.array(zod_1.z.enum(enums_1.techNameEnum)),
        /** max(established) across all recognized ingredient categories */
        observationWindowWeeks: zod_1.z.number().int().positive(),
    }),
    APIPaywallObservationWindowBaseSchema.extend({
        state: zod_1.z.literal('established'),
    }),
    APIPaywallObservationWindowBaseSchema.extend({
        state: zod_1.z.literal('protective'),
    }),
]);
//# sourceMappingURL=routine.js.map